import { APIEndpoint } from ".";
import { CashFlow } from "../types/Cashflow";

const token = localStorage.getItem("token");

// Create a new cash flow
export const createCashFlow = async (cashFlow: CashFlow, accountId: number) => {
  return fetch(`${APIEndpoint}/cashflows`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token || "",
      AccountId: accountId as any,
    },
    body: JSON.stringify(cashFlow),
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error creating cash flow:", error));
};

// Bulk create new cash flows
export const createManyCashFlows = async (
  cashFlows: CashFlow[],
  accountId: number
) => {
  return fetch(`${APIEndpoint}/cashflows/bulk`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token || "",
      AccountId: accountId as any,
    },
    body: JSON.stringify(cashFlows),
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error creating cash flows:", error));
};

export const getCashFlowsByIds = async (
  accountId: number,
  cashflowIds: number[],
  showHidden?: boolean
): Promise<CashFlow[]> => {
  const showHiddenParam = showHidden ? "&showHidden=true" : "";
  const url = `${APIEndpoint}/cashflows/byIds?ids=${cashflowIds.join(",")}${showHiddenParam}`;
  const result = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: token || "",
      AccountId: accountId as any,
    },
  });

  if (result.ok) {
    const data = await result.json();
    if (data.success && data.data) {
      const cashflows = data.data.map((x: CashFlow) => {
        return { ...x } as CashFlow;
      });

      return cashflows;
    }
  }

  return [];
};

// Read (get) all cash flows
export const getCashFlows = async (
  accountId: number,
  fromDate?: string,
  toDate?: string,
  showHidden?: boolean
) => {
  const showHiddenParam = showHidden ? "&showHidden=true" : "";
  const url =
    fromDate && toDate
      ? `${APIEndpoint}/cashflows?toDate=${toDate}&fromDate=${fromDate}${showHiddenParam}`
      : `${APIEndpoint}/cashflows?fromDate=2020-02-21T23:59:59.999Z&toDate=${new Date(
          new Date().setFullYear(new Date().getFullYear() + 1)
        ).toISOString()}${showHiddenParam}`;
  return fetch(url, {
    method: "GET",
    headers: {
      Authorization: token || "",
      AccountId: accountId as any,
    },
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error getting cash flows:", error));
};

// Update an existing cash flow
export const updateCashFlow = async (
  cashFlowId: number,
  cashFlow: CashFlow,
  accountId: number
) => {
  return fetch(`${APIEndpoint}/cashflows/${cashFlowId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: token || "",
      AccountId: accountId as any,
    },
    body: JSON.stringify(cashFlow),
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error updating cash flow:", error));
};

// Delete an existing cash flow
export const deleteCashFlow = async (cashFlowId: number, accountId: number) => {
  return fetch(`${APIEndpoint}/cashflows/${cashFlowId}`, {
    method: "DELETE",
    headers: {
      Authorization: token || "",
      AccountId: accountId as any,
    },
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error deleting cash flow:", error));
};

// Delete all cash flows
export const deleteAllCashFlows = async (accountId: number) => {
  return fetch(`${APIEndpoint}/cashflows`, {
    method: "DELETE",
    headers: {
      Authorization: token || "",
      AccountId: accountId as any,
    },
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error deleting all cash flows:", error));
};

export const updateManyCashflows = async (
  cashflows: CashFlow[],
  accountId: number
) => {
  return fetch(`${APIEndpoint}/cashflows/bulk`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: token || "",
      AccountId: accountId as any,
    },
    body: JSON.stringify(cashflows),
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error updating cashflow:", error));
};

export const getCashflowsWithoutCategory = async () => {
  const url = `${APIEndpoint}/cashflows/distinct/category`;

  return fetch(url, {
    method: "GET",
    headers: {
      Authorization: token || "",
    },
  })
    .then((response) => response.json())
    .catch((error) =>
      console.error("Error getting distinct cash flows types:", error)
    );
};

export const categorizeCashflows = async () => {
  return fetch(`${APIEndpoint}/cashflows/distinct/category`, {
    method: "POST",
    headers: {
      Authorization: token || "",
    },
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error categorizing cash flows:", error));
};

export const updateCashflowsPositionKeyField = async (
  accountId: number,
  ids: number[],
  field: string,
  value: string
) => {
  return fetch(`${APIEndpoint}/cashflows/updatePositionKeyField`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token || "",
      AccountId: accountId as any,
    },
    body: JSON.stringify({
      ids,
      field,
      value,
    }),
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error updating cashflow:", error));
};

export const updateCashflowsBookAndPositionGroup = async (
  accountId: number,
  ids: number[],
  book: string,
  positionGroup: string
) => {
  return fetch(`${APIEndpoint}/cashflows/updateBookAndPositionGroup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token || "",
      AccountId: accountId as any,
    },
    body: JSON.stringify({
      ids,
      book,
      positionGroup,
    }),
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error updating cashflow:", error));
};

// Hide cashflows by setting hidden field to true
export const hideCashflows = async (
  accountId: number,
  cashflowIds: number[]
) => {
  try {
    // Fetch the cashflows first
    const cashflows = await getCashFlowsByIds(accountId, cashflowIds);
    
    // Update the hidden field to true
    const updatedCashflows = cashflows.map(cashflow => ({
      ...cashflow,
      hidden: true
    }));
    
    // Use bulk update endpoint
    return await updateManyCashflows(updatedCashflows, accountId);
  } catch (error) {
    console.error("Error hiding cashflows:", error);
    throw error;
  }
};

// Unhide cashflows by setting hidden field to false
export const unhideCashflows = async (
  accountId: number,
  cashflowIds: number[]
) => {
  try {
    // Fetch the cashflows first (including hidden ones)
    const cashflows = await getCashFlowsByIds(accountId, cashflowIds, true);
    
    // Update the hidden field to false
    const updatedCashflows = cashflows.map(cashflow => ({
      ...cashflow,
      hidden: false
    }));
    
    // Use bulk update endpoint
    return await updateManyCashflows(updatedCashflows, accountId);
  } catch (error) {
    console.error("Error unhiding cashflows:", error);
    throw error;
  }
};

export const reImportCashFlows = async (userId: number) => {
  const url = `${APIEndpoint}/cashflows/reimport`;
  return fetch(url, {
    method: "POST",
    headers: {
      Authorization: token || "",
      userId: userId as any,
    },
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error reimporting cash flows:", error));
};
